import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { Search, FileText, ArrowRight, CheckCircle, DollarSign, ListChecks, Phone } from 'lucide-react';

const includes = [
  { title: 'Review of Facts & Documents', description: 'We examine your tax returns, IRS notices, transcripts, and any other relevant documents.' },
  { title: 'Explanation of Your Situation', description: 'A clear, plain-English explanation of where you stand with the IRS and what it means.' },
  { title: 'Likely Options Outlined', description: 'We identify the realistic options available to you — not wishful thinking, but practical paths forward.' },
  { title: 'Recommended Next Steps', description: 'A concrete action plan: what to do, in what order, and what to expect.' },
  { title: 'Scope & Pricing for Larger Work', description: 'If additional work is needed, we provide an estimated scope and pricing so there are no surprises.' },
];

const whoNeeds = [
  'You received an IRS notice and need more than a quick answer',
  'You have multiple years of unfiled returns',
  'You owe the IRS and are unsure of your options',
  'You want to understand your tax situation before committing to a service',
  'Your previous tax professional didn\'t explain things clearly',
];

export default function TaxDiagnostic() {
  useSEO({
    title: 'Tax Diagnostic Review',
    description: 'A deeper review of your tax situation. We examine your IRS transcripts and documents, explain options, and recommend clear next steps.',
  });

  return (
    <div data-testid="tax-diagnostic-page">
      {/* Hero */}
      <section className="bg-brand-navy py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-5">
              Tax Diagnostic Review
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none text-white mb-5">
              A Deeper Review of Your Tax Situation
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              We dig into your IRS transcripts, documents, and tax history to find out exactly where you stand — and what your best options are.
            </p>
            <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="diagnostic-hero-cta"
                className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md"
              >
                Start Your Tax Review
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              What's Included
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-14 max-w-lg">
              Everything you need to make an informed decision
            </h2>
          </AnimatedSection>
          <div className="space-y-4">
            {includes.map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="flex items-start gap-5 p-6 border border-slate-200 rounded-lg hover:border-brand-green/30 transition-colors">
                  <div className="w-10 h-10 rounded-md bg-brand-navy/5 flex items-center justify-center flex-shrink-0">
                    <ListChecks className="w-5 h-5 text-brand-navy" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-base text-brand-navy mb-1">{item.title}</h3>
                    <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Who Needs This */}
      <section className="py-20 lg:py-28 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
                Is This For You?
              </p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-6">
                Move from "I have a question" to "I have a plan"
              </h2>
              <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed mb-8">
                The Tax Diagnostic Review is the bridge between asking a question and getting full-service help. It's designed for people who need a deeper understanding before committing.
              </p>
              <div className="space-y-3">
                {whoNeeds.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-brand-green mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <div className="bg-white border border-slate-200 rounded-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <DollarSign className="w-6 h-6 text-brand-green" />
                  <h3 className="font-heading font-semibold text-lg text-brand-navy">Transparent Pricing</h3>
                </div>
                <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed mb-6">
                  The Tax Diagnostic Review is a fixed-fee engagement. You'll know the cost upfront before committing. No surprises, no hidden fees.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0" />
                    Fixed fee — discussed before we begin
                  </li>
                  <li className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0" />
                    No obligation to continue beyond the review
                  </li>
                  <li className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0" />
                    Written summary of findings and recommendations
                  </li>
                </ul>
                <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                  <Button
                    data-testid="diagnostic-pricing-cta"
                    className="w-full bg-brand-green hover:bg-brand-green-hover text-white font-semibold py-6 text-base rounded-md"
                  >
                    Start Your Tax Review
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Service Ladder */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4">
              Not sure if you need a diagnostic?
            </h2>
            <p className="text-base text-slate-500 mb-8 max-w-lg mx-auto">
              Start with a simple question. If the issue is more serious, we'll recommend the diagnostic. There's no pressure to commit.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/ask">
                <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-semibold px-7 py-6 text-base rounded-md">
                  Ask a Question First
                </Button>
              </Link>
              <a href="tel:8555892461">
                <Button variant="outline" className="border-slate-300 text-slate-600 hover:bg-slate-50 font-semibold px-7 py-6 text-base rounded-md">
                  <Phone className="w-4 h-4 mr-2" /> 855-589-2461
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
